import type { Reason, ReportRow, SiteProgress } from "../../../api/routing";

/** What the visits dashboard draws, worked out from the report's own rows so
 * the charts and the Report tab can never disagree. */

/** The four things a planned visit can be, in the order they stack. */
export type Series = "done" | "not_done" | "overdue" | "pending";

export const SERIES: readonly { key: Series; label: string }[] = [
  { key: "done", label: "Done" },
  { key: "not_done", label: "Not done" },
  { key: "overdue", label: "Pending, past their day" },
  { key: "pending", label: "Pending, still to come" },
];

export type Counts = Record<Series, number> & { planned: number };

export interface Bucket {
  key: string;
  label: string;
  counts: Counts;
}

export function seriesOf(row: ReportRow): Series {
  if (row.outcome === "done") {
    return "done";
  }
  if (row.outcome === "not_done") {
    return "not_done";
  }
  return row.overdue ? "overdue" : "pending";
}

function empty(): Counts {
  return { planned: 0, done: 0, not_done: 0, overdue: 0, pending: 0 };
}

export function tally(rows: readonly ReportRow[]): Counts {
  const counts = empty();
  for (const row of rows) {
    counts.planned += 1;
    counts[seriesOf(row)] += 1;
  }
  return counts;
}

/** Done as a whole percentage of planned; null when nothing was planned. */
export function completion(counts: Counts): number | null {
  return counts.planned === 0 ? null : Math.round((100 * counts.done) / counts.planned);
}

function group(rows: readonly ReportRow[], keyOf: (row: ReportRow) => string, labelOf: (row: ReportRow) => string): Bucket[] {
  const found = new Map<string, { label: string; rows: ReportRow[] }>();
  for (const row of rows) {
    const key = keyOf(row);
    const entry = found.get(key) ?? { label: labelOf(row), rows: [] };
    entry.rows.push(row);
    found.set(key, entry);
  }
  return [...found.entries()].map(([key, entry]) => ({ key, label: entry.label, counts: tally(entry.rows) }));
}

const byPlanned = (a: Bucket, b: Bucket) => b.counts.planned - a.counts.planned || a.label.localeCompare(b.label);

export function byCrew(rows: readonly ReportRow[]): Bucket[] {
  return group(
    rows,
    (row) => String(row.crew_id),
    (row) => row.crew,
  ).sort(byPlanned);
}

export const NO_REGION = "No region";

export function byRegion(rows: readonly ReportRow[]): Bucket[] {
  return group(
    rows,
    (row) => row.region ?? NO_REGION,
    (row) => row.region ?? NO_REGION,
  ).sort(byPlanned);
}

// --- over time ---------------------------------------------------------------------

/** Past this many days a column per day is too thin to read, so weeks. */
const MAX_DAY_COLUMNS = 45;

function parse(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function iso(day: Date): string {
  return day.toISOString().slice(0, 10);
}

function addDays(day: Date, n: number): Date {
  return new Date(day.getTime() + n * 86_400_000);
}

function monday(day: Date): Date {
  return addDays(day, -((day.getUTCDay() + 6) % 7));
}

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function dayLabel(day: Date): string {
  return `${WEEKDAY[day.getUTCDay()]} ${day.getUTCDate()} ${MONTH[day.getUTCMonth()]}`;
}

/** Every working day of the period, and any weekend day that had visits, so a
 * day nothing was planned for shows as a gap rather than disappearing. A long
 * period is shown by week instead. */
export function overTime(rows: readonly ReportRow[], period: { start: string; end: string }): { unit: "day" | "week"; buckets: Bucket[] } {
  const start = parse(period.start);
  const end = parse(period.end);
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
  const byDate = new Map<string, ReportRow[]>();
  for (const row of rows) {
    byDate.set(row.date, [...(byDate.get(row.date) ?? []), row]);
  }
  if (days > MAX_DAY_COLUMNS) {
    const weeks = new Map<string, ReportRow[]>();
    for (let week = monday(start); week <= end; week = addDays(week, 7)) {
      weeks.set(iso(week), []);
    }
    for (const row of rows) {
      const key = iso(monday(parse(row.date)));
      weeks.set(key, [...(weeks.get(key) ?? []), row]);
    }
    return {
      unit: "week",
      buckets: [...weeks.entries()].map(([key, inWeek]) => ({ key, label: `Week of ${dayLabel(parse(key)).slice(4)}`, counts: tally(inWeek) })),
    };
  }
  const buckets: Bucket[] = [];
  for (let day = start; day <= end; day = addDays(day, 1)) {
    const key = iso(day);
    const weekend = day.getUTCDay() === 0 || day.getUTCDay() === 6;
    const onDay = byDate.get(key) ?? [];
    if (!weekend || onDay.length > 0) {
      buckets.push({ key, label: dayLabel(day), counts: tally(onDay) });
    }
  }
  return { unit: "day", buckets };
}

/** The rows a time bucket stands for. */
export function inBucket(row: ReportRow, key: string, unit: "day" | "week"): boolean {
  return unit === "day" ? row.date === key : iso(monday(parse(row.date))) === key;
}

// --- why visits were not done --------------------------------------------------------

/** Not done before the reasons list existed: a note, and no reason to count. */
export const UNRECORDED = "unrecorded";

export interface ReasonCount {
  key: string;
  label: string;
  count: number;
}

/** Each reason with how many visits were not done for it, commonest first. */
export function reasonCounts(rows: readonly ReportRow[], reasons: readonly Reason[]): ReasonCount[] {
  const order = new Map(reasons.map((reason, index) => [reason.code, index]));
  const counts = new Map<string, ReasonCount>();
  for (const row of rows) {
    if (row.outcome !== "not_done") {
      continue;
    }
    const key = row.reason ?? UNRECORDED;
    const label = row.reason_label ?? (row.reason === null ? "Reason not recorded" : row.reason);
    const found = counts.get(key) ?? { key, label, count: 0 };
    counts.set(key, { ...found, count: found.count + 1 });
  }
  const rank = (key: string) => order.get(key) ?? Number.MAX_SAFE_INTEGER;
  return [...counts.values()].sort((a, b) => b.count - a.count || rank(a.key) - rank(b.key));
}

export function hasReason(row: ReportRow, key: string): boolean {
  return row.outcome === "not_done" && (row.reason ?? UNRECORDED) === key;
}

// --- site progress --------------------------------------------------------------------

const ASSESSED = ["assessment_completed", "service_overhaul", "service_completed", "replacement", "replacement_completed", "completed"];
const WORK_DONE = ["service_completed", "replacement_completed", "completed"];

export interface Milestone {
  key: string;
  label: string;
  count: number;
  /** Of every site, as a whole percentage. */
  share: number;
}

/** How far a set of sites has got, each step counting every site that reached
 * it, from their counts by stage. */
export function milestoneCounts(counts: Readonly<Record<string, number>>, total: number, scoped: number): Milestone[] {
  const count = (statuses: readonly string[]) => statuses.reduce((sum, status) => sum + (counts[status] ?? 0), 0);
  const steps: [string, string, number][] = [
    ["sites", "Sites", total],
    ["scoped", "Scoped (QCP on file)", scoped],
    ["assessed", "Assessed", count(ASSESSED)],
    ["work_done", "Work done", count(WORK_DONE)],
    ["completed", "Completed", count(["completed"])],
  ];
  return steps.map(([key, label, n]) => ({ key, label, count: n, share: total === 0 ? 0 : Math.round((100 * n) / total) }));
}

export function milestones(progress: SiteProgress): Milestone[] {
  const counts = Object.fromEntries(progress.stages.map((stage) => [stage.status, stage.count]));
  return milestoneCounts(counts, progress.total, progress.scoped);
}

/** A rounded top for a count axis, and the ticks up to it: 0, 2, 4, 6, 8. */
export function countScale(max: number): { top: number; ticks: number[] } {
  if (max <= 0) {
    return { top: 1, ticks: [0, 1] };
  }
  const rough = max / 4;
  const power = 10 ** Math.floor(Math.log10(rough));
  const step = Math.max(1, [1, 2, 5, 10].map((m) => m * power).find((s) => s >= rough) ?? 10 * power);
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let tick = 0; tick <= top; tick += step) {
    ticks.push(tick);
  }
  return { top, ticks };
}
